import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { PRICE_IMPACT_GRADES } from 'consts';
import { store } from 'store';
import { bn } from 'utils/BigNumber';
import { cutTokenSymbol, toDollars } from 'utils/helpers';

import * as S from './SwapDetails.styles';

export const SwapDetails = observer(() => {
  const { wowmaxResult, value, tokenIn, tokenOut, settings, openRouting, isWrapUnwrap } =
    store.swapStore;
  const [isOpen, setIsOpen] = useState(false);
  const [isPriceInverted, setIsPriceInverted] = useState(false);
  const priceNodeRef = useRef<HTMLDivElement>(null);
  const [priceNodeWidth, setPriceNodeWidth] = useState(0);

  useEffect(() => {
    setIsPriceInverted(false);
  }, [tokenIn, tokenOut]);

  const priceImpact = wowmaxResult.data ? bn(wowmaxResult.data.priceImpact) : null;
  const amountOut = wowmaxResult.data ? bn(wowmaxResult.data.amountOut) : null;

  const amountInHuman = bn(value);
  const amountOutHuman = amountOut && tokenOut ? amountOut.fromDecimals(tokenOut.decimals) : null;
  const priceTokenIn = isPriceInverted ? tokenOut : tokenIn;
  const priceTokenOut = isPriceInverted ? tokenIn : tokenOut;

  const priceInTokens = amountOutHuman
    ? isPriceInverted
      ? bn(amountInHuman).div(amountOutHuman)
      : bn(amountOutHuman).div(amountInHuman)
    : null;

  const oneTokenInDollars = wowmaxResult.data?.[isPriceInverted ? 'from' : 'to'].priceUSD;
  const priceInDollars =
    priceInTokens && oneTokenInDollars ? bn(priceInTokens).mul(oneTokenInDollars) : null;

  const priceInTokensView =
    priceTokenIn && priceInTokens && priceTokenOut
      ? `1 ${cutTokenSymbol(priceTokenIn.symbol)} = ${priceInTokens.toView()} ${cutTokenSymbol(
          priceTokenOut.symbol
        )}`
      : '';
  const priceInDollarsView = priceInDollars ? `(${toDollars(priceInDollars)})` : '';
  const priceSymbolsNumber = priceInTokensView.length + priceInDollarsView.length;

  useEffect(() => {
    const priceNode = priceNodeRef.current;
    if (!priceNode) return;

    const resizeObserver = new ResizeObserver((entries) => {
      setPriceNodeWidth(Math.floor(entries[0].contentRect.width));
    });
    resizeObserver.observe(priceNode);

    return () => {
      resizeObserver.unobserve(priceNode);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceNodeRef.current]);

  useEffect(() => {
    if (!priceNodeRef.current) return;
    setPriceNodeWidth(priceNodeRef.current.clientWidth);
  }, [priceSymbolsNumber]);

  if (!wowmaxResult.data || !tokenIn || !tokenOut || !amountOut || !priceImpact || isWrapUnwrap) {
    return null;
  }

  return (
    <S.Root>
      <S.MainBlock>
        <S.Price ref={priceNodeRef} $width={priceNodeWidth} $symbolsNumber={priceSymbolsNumber}>
          <S.PriceTokens>{priceInTokensView}</S.PriceTokens>
          <S.PriceDollars>{priceInDollarsView}</S.PriceDollars>
          <S.PriceFlipButton onClick={() => setIsPriceInverted(!isPriceInverted)}>
            ⇄
          </S.PriceFlipButton>
        </S.Price>
        <S.ExpandButton onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
          <S.Gas>
            <span>⛽</span>
            <span>{toDollars(wowmaxResult.data.gasCostUSD + wowmaxResult.data.l1FeeUSD)}</span>
          </S.Gas>
          <S.ArrowDown>▾</S.ArrowDown>
        </S.ExpandButton>
      </S.MainBlock>
      <S.ExpandableBlock $isOpen={isOpen}>
        <S.Divider />
        <S.Line>
          <S.Caption>Price impact:</S.Caption>
          <S.Value
            style={{
              color: priceImpact.gte(PRICE_IMPACT_GRADES.danger)
                ? 'rgba(231, 76, 60, 0.9)'
                : priceImpact.gte(PRICE_IMPACT_GRADES.warning)
                  ? 'rgba(241, 196, 15, 0.9)'
                  : undefined,
            }}
          >
            {priceImpact.toView(0, { signs: 4 })} %
          </S.Value>
        </S.Line>
        <S.Line>
          <S.Caption>Estimated received:</S.Caption>
          <S.Value>
            {amountOut.toView(tokenOut.decimals)} {cutTokenSymbol(tokenOut.symbol)}
          </S.Value>
        </S.Line>
        <S.Line>
          <S.Caption>
            Minimum received: <br />
            (After slippage {settings.slippage}%)
          </S.Caption>
          <S.Value>
            {amountOut
              .mul(100 - settings.slippage)
              .div(100)
              .toView(tokenOut.decimals)}{' '}
            {cutTokenSymbol(tokenOut.symbol)}
          </S.Value>
        </S.Line>
        {!!wowmaxResult.data.l1FeeUSD && (
          <S.Line>
            <S.Caption>L1 gas:</S.Caption>
            <S.Value>{toDollars(wowmaxResult.data.l1FeeUSD)}</S.Value>
          </S.Line>
        )}
        <S.Line>
          <S.Caption>{wowmaxResult.data.l1FeeUSD ? 'L2 gas' : 'Gas'}:</S.Caption>
          <S.Value>{toDollars(wowmaxResult.data.gasCostUSD)}</S.Value>
        </S.Line>
        <S.Divider />
        <S.Line>
          <S.Caption>Routing</S.Caption>
          <S.RoutingButton onClick={openRouting}>
            <S.RoutingTokens>
              <S.RoutingToken>{cutTokenSymbol(tokenIn.symbol)}</S.RoutingToken>→
              <S.RoutingToken>{cutTokenSymbol(tokenOut.symbol)}</S.RoutingToken>
            </S.RoutingTokens>
            <S.RoutingIconWrapper>↗</S.RoutingIconWrapper>
          </S.RoutingButton>
        </S.Line>
      </S.ExpandableBlock>
    </S.Root>
  );
});
